import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Separator } from "@arcadeai/design-system/components/ui/atoms/separator";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tabs";
import { GripVertical, Play, SquareCode, Wrench } from "lucide-react";
import { useMediaQuery } from "usehooks-ts";

/**
 * Skeleton for the resizable handle between panels
 */
function ResizableHandleSkeleton() {
  return (
    <div className="relative hidden w-px items-center justify-center bg-border lg:flex">
      <div className="z-10 flex h-4 w-3 items-center justify-center rounded-xs border bg-border">
        <GripVertical className="size-2.5" />
      </div>
    </div>
  );
}

function InputSkeleton() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-10 w-full rounded-md" />
    </div>
  );
}

function ToolSelectorSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      <span className="font-medium text-muted-foreground text-sm">
        Select Tool
      </span>
      <div className="flex h-auto w-full items-center justify-between gap-4 rounded-md border border-neutral-200/60 bg-neutral-100/50 px-4 py-3 dark:border-neutral-800 dark:bg-neutral-900/50">
        <div className="flex items-center gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-neutral-50 to-neutral-100 shadow-sm ring-1 ring-neutral-200/80 dark:from-neutral-800 dark:to-neutral-900 dark:ring-neutral-700/80">
            <Skeleton className="h-6 w-6 rounded" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
        <Skeleton className="h-5 w-5 shrink-0 rounded" />
      </div>
    </div>
  );
}

/**
 * Skeleton for the form panel while tools/toolkits are loading
 */
function FormPanelSkeleton() {
  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border border-neutral-300 bg-neutral-50 lg:flex-1 dark:border-neutral-800 dark:bg-neutral-950">
      {/* Header - hidden on mobile where tabs are used instead */}
      <div className="hidden min-h-[46px] items-center gap-2 border-neutral-200 border-b bg-neutral-100 px-4 lg:flex dark:border-neutral-800/80 dark:bg-neutral-900/80">
        <Wrench className="h-3.5 w-3.5 text-neutral-500 dark:text-neutral-400" />
        <span className="font-medium text-neutral-600 text-xs uppercase tracking-widest dark:text-neutral-300">
          Tool Setup
        </span>
      </div>

      {/* Content - matches ScrollArea structure */}
      <div className="min-h-0 flex-1 overflow-hidden">
        <div className="space-y-4 px-4 pt-5 pb-2">
          <ToolSelectorSkeleton />
        </div>

        {/* Matches FormPanel's px-4 pb-4 wrapper around ToolForm */}
        <div className="px-4 pb-4">
          {/* Matches ToolForm's flex flex-col gap-4 px-1.5 */}
          <div className="flex flex-col gap-4 px-1.5">
            {/* Tool Overview - matches mt-2 space-y-2 */}
            <div className="mt-2 space-y-2">
              <h2 className="font-medium text-base">Tool Overview</h2>
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-4/5" />
              </div>
            </div>

            <Separator className="my-0" />

            {/* Parameters section - matches space-y-4 */}
            <div className="space-y-4">
              <h3 className="font-medium text-muted-foreground text-sm">
                Parameters
              </h3>
              <div className="space-y-5">
                {/* Required group - matches space-y-4 */}
                <div className="space-y-4">
                  <h4 className="font-medium text-foreground text-sm">
                    Required
                  </h4>
                  <InputSkeleton />
                  <InputSkeleton />
                </div>
              </div>
            </div>

            {/* Execute button wrapper - matches mt-6 space-y-2 */}
            <div className="mt-6 space-y-2">
              <Button className="w-full gap-2" disabled size="lg" type="button">
                <Play className="size-5" />
                Execute Tool
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for the response panel (terminal output) while tools/toolkits are loading
 */
function ResponsePanelSkeleton() {
  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden rounded-xl border border-neutral-300 bg-neutral-50 shadow-sm lg:flex-1 dark:border-neutral-800/80 dark:bg-neutral-950/95">
      {/* Terminal Header */}
      <div className="relative z-10 flex min-h-[46px] items-center justify-between border-neutral-200 border-b bg-neutral-100 px-4 dark:border-neutral-800/80 dark:bg-neutral-900/80">
        <div className="flex items-center gap-2">
          <SquareCode className="h-3.5 w-3.5 text-neutral-500 dark:text-neutral-400" />
          <span className="font-medium text-neutral-600 text-xs uppercase tracking-widest dark:text-neutral-300">
            Execution Output
          </span>
        </div>
        {/* Actual disabled Run button */}
        <Button
          className="h-6 gap-1.5 bg-emerald-600 text-white text-xs hover:bg-emerald-500 dark:bg-emerald-600 dark:hover:bg-emerald-500"
          disabled
          size="sm"
        >
          <Play className="h-3 w-3" />
          Run
        </Button>
      </div>

      {/* Terminal Content */}
      <div className="relative z-10 flex-1 overflow-auto p-4 font-mono">
        <div className="space-y-5">
          {/* Prompt skeleton */}
          <div className="flex flex-wrap items-center gap-1.5 text-sm">
            <Skeleton className="h-4 w-20 bg-neutral-200 dark:bg-neutral-800" />
            <span className="text-neutral-400 dark:text-neutral-500">/</span>
            <Skeleton className="h-4 w-16 bg-neutral-200 dark:bg-neutral-800" />
            <span className="text-neutral-400 dark:text-neutral-500">›</span>
            <Skeleton className="h-4 w-28 bg-neutral-200 dark:bg-neutral-800" />
          </div>

          {/* Expected output block - matches TerminalBlock */}
          <div className="ml-0.5">
            <div className="overflow-hidden rounded-lg border border-neutral-200 bg-neutral-100/60 backdrop-blur dark:border-neutral-800/80 dark:bg-neutral-950/60">
              <div className="flex items-center justify-between border-neutral-200 border-b bg-neutral-100/80 px-3 py-2 text-xs dark:border-neutral-800/60 dark:bg-neutral-950/40">
                <div className="flex items-center gap-2 font-mono text-neutral-500 dark:text-neutral-400">
                  <span className="rounded-md border border-neutral-300 bg-neutral-200/60 px-1.5 py-0.5 text-[10px] text-neutral-600 uppercase tracking-wider dark:border-neutral-700/80 dark:bg-neutral-900/60 dark:text-neutral-300">
                    txt
                  </span>
                  <span>Expected Output</span>
                </div>
              </div>
              <div className="space-y-1.5 px-3 pt-1 pb-3">
                <Skeleton className="h-4 w-full bg-neutral-200 dark:bg-neutral-800" />
                <Skeleton className="h-4 w-3/4 bg-neutral-200 dark:bg-neutral-800" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Terminal Footer */}
      <div className="relative z-10 flex min-h-[34px] items-center justify-between border-neutral-200 border-t bg-neutral-100 px-4 text-xs dark:border-neutral-800/80 dark:bg-neutral-900/70">
        <div className="flex items-center gap-2 font-mono text-neutral-500 dark:text-neutral-400">
          <span className="text-neutral-400 dark:text-neutral-500">Status</span>
          <span className="text-neutral-500 dark:text-neutral-400">Ready</span>
        </div>
        <div className="font-mono text-neutral-400 dark:text-neutral-500">
          Press Run to execute
        </div>
      </div>
    </div>
  );
}

/**
 * Full page skeleton for the execute tool playground, used as Suspense fallback
 */
export function PlaygroundExecuteToolSkeleton() {
  const isCompact = useMediaQuery("(max-width: 1023px)");

  if (isCompact) {
    return (
      <Tabs className="flex h-full flex-col gap-2" defaultValue="tool">
        <TabsList className="w-full">
          <TabsTrigger className="flex-1 gap-2" value="tool">
            <Wrench className="h-4 w-4" />
            Setup
          </TabsTrigger>
          <TabsTrigger className="flex-1 gap-2" value="output">
            <SquareCode className="h-4 w-4" />
            Output
          </TabsTrigger>
        </TabsList>
        <TabsContent className="min-h-0 flex-1" value="tool">
          <FormPanelSkeleton />
        </TabsContent>
        <TabsContent className="min-h-0 flex-1" value="output">
          <ResponsePanelSkeleton />
        </TabsContent>
      </Tabs>
    );
  }

  return (
    <div className="flex h-full w-full flex-col gap-4 lg:flex-row">
      <FormPanelSkeleton />
      <ResizableHandleSkeleton />
      <ResponsePanelSkeleton />
    </div>
  );
}
